import React from 'react';
import {
  Box,
  Typography,
  Container,
  Link,
  Divider
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getVersionInfo } from '../../config/version';
import { DEPLOYMENT_CONFIG } from '../../config/deploymentConfig';

function Footer() {
  const currentYear = new Date().getFullYear();
  const versionInfo = getVersionInfo();
  const { appName, companyName, supportEmail, tagline } = DEPLOYMENT_CONFIG.labels;

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        mt: 'auto' // Push footer to bottom
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          {/* Main Footer Content */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: 3,
              textAlign: { xs: 'center', md: 'left' }
            }}
          >
            {/* Product Info Section */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  mb: 1
                }}
              >
                {appName}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                {tagline}
              </Typography>
            </Box>

            {/* Contact/Support Section */}
            <Box sx={{ flex: 1, maxWidth: { xs: '100%', md: 250 } }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
              >
                Support & Contact
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Technical Issues:</strong>
                </Typography>
                <Link
                  href={`mailto:${supportEmail}`}
                  color="primary"
                  variant="caption"
                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {supportEmail}
                </Link>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Access Requests:</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Contact your administrator for access
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Bottom Footer */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              textAlign: { xs: 'center', sm: 'left' }
            }}
          >
            {/* Copyright */}
            <Typography variant="caption" color="text.secondary">
              © {currentYear} {companyName}. All rights reserved.
            </Typography>

            {/* Version & Tech Info */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                gap: { xs: 1, sm: 3 }
              }}
            >
              <Typography variant="caption" color="text.secondary">
                React v{React.version}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Firebase Integration
              </Typography>
              <Link
                component={RouterLink}
                to="/release-notes"
                color="primary.main"
                variant="caption"
                sx={{ 
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': { 
                    textDecoration: 'underline' 
                  }
                }}
              >
                {versionInfo.fullVersion} - Release Notes
              </Link>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
